import React, { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, } from 'react-router-dom'
import { Provider } from "react-redux";
import { FirebaseProvider, store } from "common";
import { FirebaseConfig } from './config/FirebaseConfig';
import { GoogleMapApiConfig } from './config/GoogleMapApiConfig';
import { GoogleMapApiOptions } from './mapConfig';
import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import { ThemeProvider } from '@mui/styles';
import { createTheme } from '@mui/material';
import { useJsApiLoader } from '@react-google-maps/api';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { webClientId } from "config/ClientIds.js";
import { HelmetProvider } from "react-helmet-async";
import CircularLoading from "./components/CircularLoading";

const LandingPage = lazy(() => import("./views/LandingPage.js"));
const LoginPage = lazy(() => import("./views/LoginPage.js"));
const PrivacyPolicy = lazy(() => import("./views/PrivacyPolicy.js"));
const AboutUs = lazy(() => import("./views/AboutUs"));
const AuthLoading = lazy(() => import('./views/AuthLoading'));
const ProtectedRoute = lazy(() => import('./views/ProtectedRoute'));
const MyProfile = lazy(() => import('./views/MyProfile'));
const BookingHistory = lazy(() => import('./views/BookingHistory'));
const Dashboard = lazy(() => import('./views/Dashboard'));
const CarTypes = lazy(() => import('./views/CarTypes'));
const AddBookings = lazy(() => import('./views/AddBookings'));
const Promos = lazy(() => import('./views/Promos'));
const Users = lazy(() => import('./views/Users'));
const CustomerDetails = lazy(() => import("views/CustomerDetails.js"));
const Notifications = lazy(() => import('./views/Notifications'));
const Settings = lazy(() => import('./views/Settings.js'));
const Complain = lazy(() => import("views/Complain.js"));
const AddMoney = lazy(() => import("./views/AddMoney"));
const Withdraws = lazy(() => import('./views/Withdraws'));
const AllReports = lazy(() => import("./views/AllReports"));
const ContactUs = lazy(() => import("./views/ContactUs"));
const UserWallet = lazy(() => import("./views/UserWallet"));
const CarsList = lazy(() => import("./views/CarsList"));
const TermCondition = lazy(() => import("views/TermCondition.js"));
const Sos = lazy(() => import('./views/Sos'));
const DriverDetails = lazy(() => import("views/DriverDetails.js"));
const Error = lazy(() => import("views/Error.js"));
const BookingDetails = lazy(() => import("views/BookingDetails.js"));
const UserDocuments = lazy(() => import("views/UserDocuments.js"));
const AddNotifications = lazy(() => import("views/AddNotifications.js"));
const EditPromo = lazy(() => import("views/EditPromo.js"));
const EditCar = lazy(() => import("views/EditCar.js"));
const EditCarType = lazy(() => import("views/EditCarType.js"));
const EditUser = lazy(() => import("views/EditUser.js"));
const PaymentSettings = lazy(() => import("views/PaymentSettings.js"));
// Use the libraries defined in GoogleMapApiOptions
const libraries = GoogleMapApiOptions.libraries;

i18n
  .use(initReactI18next)
  .init({
    resources: {},
    fallbackLng: "en",
    ns: ["translations"],
    defaultNS: "translations",
    interpolation: {
      escapeValue: false
    }
  });

function App() {

  useJsApiLoader({
    id: 'google-map',
    googleMapsApiKey: GoogleMapApiConfig,
    version: GoogleMapApiOptions.v,
    libraries
  })

  const theme = createTheme()

  return (
    <HelmetProvider>
      <Provider store={store}>
        <FirebaseProvider config={FirebaseConfig}>
          <GoogleOAuthProvider clientId={webClientId}>
            <ThemeProvider theme={theme}>
              <AuthLoading>
                <BrowserRouter future={{ v7_relativeSplatPath: true, v7_startTransition: true }}>
                  <Suspense fallback={<CircularLoading />}>
                    <Routes>
                      <Route path="/dashboard" element={<ProtectedRoute permit={"admin,fleetadmin"}><Dashboard /></ProtectedRoute>} />
                      <Route path="/bookings" element={<ProtectedRoute permit={"customer,admin,driver,fleetadmin"}><BookingHistory /></ProtectedRoute>} />
                      <Route path="/bookings/bookingdetails/:id" element={<ProtectedRoute permit={"customer,admin,driver,fleetadmin"}><BookingDetails /></ProtectedRoute>} />
                      <Route path="/profile" element={<ProtectedRoute permit={"customer,admin,driver,fleetadmin"}><MyProfile /></ProtectedRoute>} />
                      <Route path="/cartypes" element={<ProtectedRoute permit={"admin"}><CarTypes /></ProtectedRoute>} />
                      <Route path="/cars" element={<ProtectedRoute permit={"admin,fleetadmin,driver"}><CarsList /></ProtectedRoute>} />
                      <Route path="/addbookings" element={<ProtectedRoute permit={"admin,fleetadmin,customer"}><AddBookings /></ProtectedRoute>} />
                      <Route path="/promos" element={<ProtectedRoute permit={"admin"}><Promos /></ProtectedRoute>} />
                      <Route path="/users/:id" element={<ProtectedRoute permit={"admin,fleetadmin"}><Users /></ProtectedRoute>} />
                      <Route path="/users/customerdetails/:id" element={<ProtectedRoute permit={"admin,fleetadmin"}><CustomerDetails /></ProtectedRoute>} />
                      <Route path="/users/driverdetails/:id" element={<ProtectedRoute permit={"admin,fleetadmin"}><DriverDetails /></ProtectedRoute>} />
                      <Route path="/users/userdocuments/:rId/:id" element={<ProtectedRoute permit={"admin,fleetadmin"}><UserDocuments /></ProtectedRoute>} />
                      <Route path="/notifications" element={<ProtectedRoute permit={"admin"}><Notifications /></ProtectedRoute>} />
                      <Route path="/addtowallet" element={<ProtectedRoute permit={"admin"}><AddMoney /></ProtectedRoute>} />
                      <Route path="/userwallet" element={<ProtectedRoute permit={"customer,driver,fleetadmin"}><UserWallet /></ProtectedRoute>} />
                      <Route path="/withdraws" element={<ProtectedRoute permit={"admin"}><Withdraws /></ProtectedRoute>} />
                      <Route path="/sos" element={<ProtectedRoute permit={"admin"}><Sos /></ProtectedRoute>} />
                      <Route path="/complain" element={<ProtectedRoute permit={"admin"}><Complain /></ProtectedRoute>} />
                      <Route path="/allreports" element={<ProtectedRoute permit={"admin,fleetadmin"}><AllReports /></ProtectedRoute>} />
                      <Route path="/settings" element={<ProtectedRoute permit={"admin"}><Settings /></ProtectedRoute>} />
                      <Route path="/paymentsettings" element={<ProtectedRoute permit={"admin"}><PaymentSettings /></ProtectedRoute>} />
                      <Route path="/contact-us" element={<ContactUs />} />
                      <Route path="/about-us" element={<AboutUs />} />
                      <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                      <Route path="/term-condition" element={<TermCondition />} />
                      <Route path="/login" element={<LoginPage />} />
                      <Route path="/users/edituser/:usertype" element={<ProtectedRoute permit={"admin,fleetadmin"}><EditUser /></ProtectedRoute>} />
                      <Route path="/users/edituser/:usertype/:id" element={<ProtectedRoute permit={"admin,fleetadmin"}><EditUser /></ProtectedRoute>} />
                      <Route path="/notifications/addnotifications" element={<ProtectedRoute permit={"admin"}><AddNotifications /></ProtectedRoute>} />
                      <Route path="/cars/editcar" element={<ProtectedRoute permit={"admin,fleetadmin,driver"}><EditCar /></ProtectedRoute>} />
                      <Route path="/cars/editcar/:id" element={<ProtectedRoute permit={"admin,fleetadmin,driver"}><EditCar /></ProtectedRoute>} />
                      <Route path="/promos/editpromo" element={<ProtectedRoute permit={"admin"}><EditPromo /></ProtectedRoute>} />
                      <Route path="/promos/editpromo/:id" element={<ProtectedRoute permit={"admin"}><EditPromo /></ProtectedRoute>} />
                      <Route path="/contact-us" element={<ContactUs />} />
                      <Route path="/cartypes/editcartype" element={<ProtectedRoute permit={"admin,fleetadmin,driver"} ><EditCarType /></ProtectedRoute>} />
                      <Route path="/cartypes/editcartype/:id" element={<ProtectedRoute permit={"admin,fleetadmin,driver"}><EditCarType /></ProtectedRoute>} />
                      <Route path="/*" element={<Error />} />
                      <Route path="/" element={<LandingPage />} />
                    </Routes>
                  </Suspense>
                </BrowserRouter>
              </AuthLoading>
            </ThemeProvider>
          </GoogleOAuthProvider>
        </FirebaseProvider>
      </Provider>
    </HelmetProvider>
  );
}

export default App;